import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  DashboardCardType,
  OnboardingStatus,
  type DashboardLayoutItem,
  type DashboardWidgetSize,
  type OnboardingSubmissionDto,
  type PeopleAnalyticsDto,
  type ResolvedDashboardCardDto,
} from '@ciimar/shared';
import { api } from '../api/client';
import {
  BarChart,
  CategoryBarChart,
  DonutChart,
  HBarChart,
  YearCompareChart,
} from '../components/Charts';
import { KpiGlyph } from '../components/kpiIcons';
import { useIntro } from '../lib/intro';
import { pickText, useI18n, useLocalizedLabel, useT } from '../lib/i18n';
import { useBranding } from '../branding/BrandingContext';
import { useAuth } from '../auth/AuthContext';
import { STATUS_CLASS, useStatusLabels, useFormatDate } from './status';

/**
 * Painel — a personal, rearrangeable overview. The admin decides which cards a
 * user is entitled to (built-in KPIs/charts + any custom cards assigned to their
 * role/group); each user then arranges their own view: reorder by drag, resize
 * (small/medium/large, kept within a framed grid), and add/remove tiles. The
 * arrangement is saved per user; with none saved, everyone gets the full default.
 */

interface Widget {
  id: string;
  title: string;
  defaultSize: DashboardWidgetSize;
  render: () => ReactNode;
}

const SIZES: DashboardWidgetSize[] = ['sm', 'md', 'lg'];
const SIZE_GLYPH: Record<DashboardWidgetSize, string> = { sm: 'P', md: 'M', lg: 'G' };

export function Dashboard() {
  const t = useT();
  const { lang } = useI18n();
  const loc = useLocalizedLabel();
  const branding = useBranding();
  const { user } = useAuth();
  const statusLabels = useStatusLabels();
  const formatDate = useFormatDate();
  const intro = useIntro() ? ' intro' : '';

  const [analytics, setAnalytics] = useState<PeopleAnalyticsDto | null>(null);
  const [pending, setPending] = useState<OnboardingSubmissionDto[]>([]);
  const [custom, setCustom] = useState<ResolvedDashboardCardDto[]>([]);
  const [saved, setSaved] = useState<DashboardLayoutItem[] | null>(null);
  const [items, setItems] = useState<DashboardLayoutItem[] | null>(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const dragId = useRef<string | null>(null);

  useEffect(() => {
    Promise.all([
      api.peopleAnalytics(),
      api.listSubmissions({ status: OnboardingStatus.PendingReview }),
      api.listSubmissions({ status: OnboardingStatus.Approved }),
      api.dashboardCards().catch(() => [] as ResolvedDashboardCardDto[]),
      api.dashboardLayout().catch(() => ({ items: [] as DashboardLayoutItem[] })),
    ])
      .then(([a, review, approved, cards, layout]) => {
        setAnalytics(a);
        setPending([...review, ...approved].sort((x, y) => x.createdAt.localeCompare(y.createdAt)));
        setCustom(cards);
        setSaved(layout.items);
      })
      .catch((err) => setError((err as Error).message))
      .finally(() => setLoading(false));
  }, []);

  // The full catalog of tiles the user is entitled to, in the default order.
  const catalog: Widget[] = [];
  const kpi = (
    id: string,
    label: string,
    value: number | undefined,
    tone: string,
    icon: ReactNode,
  ) =>
    catalog.push({
      id,
      title: label,
      defaultSize: 'sm',
      render: () => <KpiCard label={label} value={value} tone={tone} icon={icon} />,
    });
  kpi('kpi.active', t('dashboard.kpi.active'), analytics?.totalActive, 'kpi-active', ICONS.users);
  kpi(
    'kpi.doctorate',
    t('dashboard.kpi.doctorate'),
    analytics?.totalDoctorate,
    'kpi-phd',
    ICONS.cap,
  );
  kpi(
    'kpi.admin',
    t('dashboard.kpi.adminServices'),
    analytics?.totalAdminServices,
    'kpi-admin',
    ICONS.briefcase,
  );
  kpi('kpi.pending', t('dashboard.kpi.pending'), pending.length, 'kpi-pending', ICONS.clock);

  catalog.push({
    id: 'chart.started',
    title: t('dashboard.charts.started.title'),
    defaultSize: 'md',
    render: () => (
      <ChartTile
        title={t('dashboard.charts.started.title')}
        sub={t('dashboard.charts.last12Months')}
      >
        <BarChart
          data={(analytics?.contractsStartedByMonth ?? []).map((m) => ({
            key: m.month,
            value: m.count,
          }))}
          color="var(--accent)"
          ariaLabel={t('dashboard.charts.started.aria')}
        />
      </ChartTile>
    ),
  });
  catalog.push({
    id: 'chart.ended',
    title: t('dashboard.charts.ended.title'),
    defaultSize: 'md',
    render: () => (
      <ChartTile title={t('dashboard.charts.ended.title')} sub={t('dashboard.charts.last12Months')}>
        <BarChart
          data={(analytics?.contractsEndedByMonth ?? []).map((m) => ({
            key: m.month,
            value: m.count,
          }))}
          color="var(--bad)"
          ariaLabel={t('dashboard.charts.ended.aria')}
        />
      </ChartTile>
    ),
  });
  catalog.push({
    id: 'chart.yearCompare',
    title: t('dashboard.charts.yearCompare.title'),
    defaultSize: 'md',
    render: () => (
      <ChartTile
        title={t('dashboard.charts.yearCompare.title')}
        sub={t('dashboard.charts.yearCompare.subtitle')}
      >
        <YearCompareChart
          data={analytics?.yearComparison ?? []}
          ariaLabel={t('dashboard.charts.yearCompare.aria')}
        />
        <YearLegend />
      </ChartTile>
    ),
  });

  for (const c of custom) {
    const label = loc({ label: c.label, labelEn: c.labelEn });
    if (c.type === DashboardCardType.Count) {
      catalog.push({
        id: `custom.${c.id}`,
        title: label,
        defaultSize: 'sm',
        render: () => (
          <div
            className={`stat-card ${
              c.article?.metric === 'pubsGrowthRate'
                ? (c.value ?? 0) >= 0
                  ? 'kpi-good'
                  : 'kpi-bad'
                : (c.tone ?? 'kpi-active')
            }`}
          >
            <div className="stat-top">
              <span className="stat-label">{label}</span>
              <span className="stat-ic">
                <KpiGlyph name={c.icon} />
              </span>
            </div>
            <span className="stat-value">
              {c.article?.metric === 'pubsGrowthRate' && (c.value ?? 0) > 0 ? '+' : ''}
              {c.value ?? 0}
              {c.unit ?? ''}
            </span>
          </div>
        ),
      });
    } else {
      catalog.push({
        id: `custom.${c.id}`,
        title: label,
        defaultSize: 'md',
        render: () => (
          <ChartTile title={label}>
            <CustomChart card={c} analytics={analytics} />
          </ChartTile>
        ),
      });
    }
  }

  catalog.push({
    id: 'panel.pending',
    title: t('dashboard.pending.title'),
    defaultSize: 'lg',
    render: () => (
      <div className="erp-panel">
        <div className="erp-toolbar">
          <strong>{t('dashboard.pending.title')}</strong>
          <span className="page-sub">{pending.length}</span>
        </div>
        {pending.length === 0 ? (
          <div className="panel-empty is-empty">{t('dashboard.pending.empty')}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="grid">
              <thead>
                <tr>
                  <th>{t('dashboard.table.name')}</th>
                  <th>{t('dashboard.table.email')}</th>
                  <th>{t('dashboard.table.department')}</th>
                  <th>{t('dashboard.table.received')}</th>
                  <th>{t('dashboard.table.status')}</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <Link to={`/submissions/${r.id}`}>{r.fullName}</Link>
                    </td>
                    <td>{r.email}</td>
                    <td>{r.department ?? '—'}</td>
                    <td>{formatDate(r.createdAt)}</td>
                    <td>
                      <span className={STATUS_CLASS[r.status]}>{statusLabels[r.status]}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    ),
  });

  // Initialise the arrangement once data + the saved layout have loaded.
  useEffect(() => {
    if (saved === null || catalog.length === 0 || items !== null) return;
    const ids = new Set(catalog.map((w) => w.id));
    setItems(
      saved.length > 0
        ? saved.filter((it) => ids.has(it.id))
        : catalog.map((w) => ({ id: w.id, size: w.defaultSize })),
    );
    // catalog is rebuilt every render; the `items !== null` guard stops re-runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved, catalog.length, items]);

  const current = items ?? [];
  const byId = new Map(catalog.map((w) => [w.id, w]));
  const visible = current
    .map((it) => ({ it, widget: byId.get(it.id) }))
    .filter((x): x is { it: DashboardLayoutItem; widget: Widget } => Boolean(x.widget));
  const available = catalog.filter((w) => !current.some((it) => it.id === w.id));

  function commit(next: DashboardLayoutItem[]) {
    setItems(next);
    void api.saveDashboardLayout({ items: next }).catch(() => {});
  }
  const resize = (id: string, size: DashboardWidgetSize) =>
    commit(current.map((it) => (it.id === id ? { ...it, size } : it)));
  const remove = (id: string) => commit(current.filter((it) => it.id !== id));
  const add = (id: string) => {
    const w = byId.get(id);
    if (w) commit([...current, { id, size: w.defaultSize }]);
  };
  const reset = () => {
    setItems(catalog.map((w) => ({ id: w.id, size: w.defaultSize })));
    void api.saveDashboardLayout({ items: [] }).catch(() => {});
  };
  function reorder(from: string | null, to: string) {
    if (!from || from === to) return;
    const arr = [...current];
    const fi = arr.findIndex((x) => x.id === from);
    const ti = arr.findIndex((x) => x.id === to);
    if (fi < 0 || ti < 0) return;
    const [moved] = arr.splice(fi, 1);
    arr.splice(ti, 0, moved);
    commit(arr);
  }

  return (
    <section>
      <div className="page-head">
        <div className="page-head-text">
          <h2>{t('dashboard.title')}</h2>
          <p className="page-sub">{t('dashboard.subtitle')}</p>
        </div>
        <div className="spacer" />
        {!loading && (
          <div className="dash-head-actions">
            {editing && (
              <button type="button" className="secondary" onClick={reset}>
                {t('dashboard.customize.reset')}
              </button>
            )}
            <button
              type="button"
              className={editing ? '' : 'secondary'}
              onClick={() => setEditing((v) => !v)}
            >
              {editing ? t('dashboard.customize.done') : t('dashboard.customize.edit')}
            </button>
            {!editing && (
              <Link to="/people">
                <button type="button" className="secondary">
                  {t('dashboard.viewPeople')}
                </button>
              </Link>
            )}
          </div>
        )}
      </div>

      {error && <div className="alert error">{error}</div>}
      {loading ? (
        <p className="muted">{t('dashboard.loading')}</p>
      ) : (
        <>
          {!editing && (
            <WelcomeBanner
              name={user?.displayName}
              pendingCount={pending.length}
              icon={branding?.icon}
            />
          )}
          {custom.length > 0 && (
            <div className="page-sub" style={{ margin: '4px 0 10px' }}>
              {pickText(branding?.dashboardSectionLabel, branding?.dashboardSectionLabelEn, lang) ||
                t('shared.customCardsHeading')}
            </div>
          )}

          <div className={`dash-grid${editing ? ' editing' : ''}${intro}`}>
            {visible.map(({ it, widget }) => (
              <div
                key={it.id}
                className={`dash-tile dash-${it.size}`}
                draggable={editing}
                onDragStart={() => {
                  dragId.current = it.id;
                }}
                onDragOver={editing ? (e) => e.preventDefault() : undefined}
                onDrop={editing ? () => reorder(dragId.current, it.id) : undefined}
              >
                {editing && (
                  <div className="dash-bar" title={t('dashboard.customize.dragHint')}>
                    <span className="dash-grip" aria-hidden="true">
                      ⠿
                    </span>
                    <span className="dash-title">{widget.title}</span>
                    <div className="dash-sizes" role="group">
                      {SIZES.map((s) => (
                        <button
                          key={s}
                          type="button"
                          className={`dash-size-btn${it.size === s ? ' on' : ''}`}
                          onClick={() => resize(it.id, s)}
                          title={t(`dashboard.customize.size.${s}`)}
                        >
                          {SIZE_GLYPH[s]}
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="dash-remove"
                      onClick={() => remove(it.id)}
                      title={t('common.remove')}
                      aria-label={t('common.remove')}
                    >
                      ✕
                    </button>
                  </div>
                )}
                {widget.render()}
              </div>
            ))}
          </div>

          {visible.length === 0 && !editing && (
            <div className="panel-empty">{t('dashboard.customize.empty')}</div>
          )}

          {editing && (
            <div className="dash-add">
              <strong>{t('dashboard.customize.addTitle')}</strong>
              {available.length === 0 ? (
                <span className="muted">{t('dashboard.customize.allAdded')}</span>
              ) : (
                available.map((w) => (
                  <button key={w.id} type="button" className="secondary" onClick={() => add(w.id)}>
                    + {w.title}
                  </button>
                ))
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

/** A chart tile: a titled card wrapping a chart (and optional legend). */
function ChartTile({ title, sub, children }: { title: string; sub?: string; children: ReactNode }) {
  return (
    <div className="chart-card">
      <div className="chart-card-head">
        <strong>{title}</strong>
        {sub && <span className="page-sub">{sub}</span>}
      </div>
      {children}
    </div>
  );
}

function YearLegend() {
  const t = useT();
  return (
    <div className="chart-legend">
      <span className="legend-prev">
        <i />
        {t('dashboard.legend.previousYear')}
      </span>
      <span className="legend-cur">
        <i />
        {t('dashboard.legend.currentYear')}
      </span>
      <span className="legend-fc">
        <i />
        {t('dashboard.legend.forecast')}
      </span>
    </div>
  );
}

/** Renders a configured chart card: people series from analytics, else the
 *  article series the backend resolved onto the card (`chartData`). */
function CustomChart({
  card,
  analytics,
}: {
  card: ResolvedDashboardCardDto;
  analytics: PeopleAnalyticsDto | null;
}) {
  const t = useT();
  const kind = card.chart?.chart ?? 'started';
  const articleSeries = card.chartData ?? [];
  if (kind === 'fieldDistribution') {
    // Free-form distribution over a ficha field; backend fills chartData.
    const style = card.chart?.style ?? 'bar';
    const aria = card.label || t('dashboard.custom.distribution');
    if (style === 'donut') return <DonutChart data={articleSeries} ariaLabel={aria} />;
    if (style === 'hbar') return <HBarChart data={articleSeries} ariaLabel={aria} />;
    return <CategoryBarChart data={articleSeries} ariaLabel={aria} />;
  }
  if (kind === 'pubsByYear') {
    return <CategoryBarChart data={articleSeries} ariaLabel={t('dashboard.custom.pubsByYear')} />;
  }
  if (kind === 'pubsByMonth') {
    // Series labels are 'yyyy-mm'; BarChart localizes them to month ticks.
    return (
      <BarChart
        data={articleSeries.map((d) => ({ key: d.label, value: d.value }))}
        ariaLabel={t('dashboard.custom.pubsByMonth')}
      />
    );
  }
  if (kind === 'pubsYearCompare') {
    return (
      <>
        <YearCompareChart
          data={card.yearCompare ?? []}
          ariaLabel={t('dashboard.custom.pubsYearCompareAria')}
        />
        <YearLegend />
      </>
    );
  }
  if (kind === 'citationsByYear') {
    return (
      <CategoryBarChart
        data={articleSeries}
        color="var(--info)"
        ariaLabel={t('dashboard.custom.citationsByYear')}
      />
    );
  }
  if (kind === 'quartileDist') {
    return <DonutChart data={articleSeries} ariaLabel={t('dashboard.custom.quartileDist')} />;
  }
  if (kind === 'pubsByTeam') {
    return <HBarChart data={articleSeries} ariaLabel={t('dashboard.custom.pubsByTeam')} />;
  }
  if (kind === 'topResearchers') {
    return <HBarChart data={articleSeries} ariaLabel={t('dashboard.custom.topResearchers')} />;
  }
  if (kind === 'yearCompare') {
    return (
      <>
        <YearCompareChart
          data={analytics?.yearComparison ?? []}
          ariaLabel={t('dashboard.custom.yearCompareAria')}
        />
        <YearLegend />
      </>
    );
  }
  const series =
    kind === 'ended' ? analytics?.contractsEndedByMonth : analytics?.contractsStartedByMonth;
  return (
    <BarChart
      data={(series ?? []).map((m) => ({ key: m.month, value: m.count }))}
      color={kind === 'ended' ? 'var(--bad)' : 'var(--accent)'}
      ariaLabel={t('dashboard.custom.contractsByMonth')}
    />
  );
}

/** Line icons for the KPI badges (stroke = currentColor, tinted per tone). */
const ICONS = {
  users: (
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  ),
  cap: <path d="M22 10 12 5 2 10l10 5 10-5zM6 12v5c0 1 2.5 2.5 6 2.5s6-1.5 6-2.5v-5" />,
  briefcase: (
    <path d="M20 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2zM16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
  ),
  clock: <path d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 7v5l3 2" />,
};

function KpiCard({
  label,
  value,
  tone,
  icon,
}: {
  label: string;
  value?: number;
  tone: string;
  icon: ReactNode;
}) {
  return (
    <div className={`stat-card ${tone}`}>
      <div className="stat-top">
        <span className="stat-label">{label}</span>
        <span className="stat-ic">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {icon}
          </svg>
        </span>
      </div>
      <span className="stat-value">{value ?? 0}</span>
    </div>
  );
}

/** Greeting by local time of day: morning (<12h), afternoon (<20h), evening. */
function greetingKey(hour: number): string {
  if (hour < 12) return 'dashboard.welcome.morning';
  if (hour < 20) return 'dashboard.welcome.afternoon';
  return 'dashboard.welcome.evening';
}

/**
 * Navy welcome banner atop the Painel: who is signed in and the one thing
 * that most needs attention (requests to handle), with a shortcut to it.
 */
function WelcomeBanner({
  name,
  pendingCount,
  icon,
}: {
  name?: string;
  pendingCount: number;
  icon?: string | null;
}) {
  const t = useT();
  const first = name?.trim().split(/\s+/)[0];
  const greeting = t(greetingKey(new Date().getHours()));
  return (
    <div className="dash-hero">
      {icon && <img className="dash-hero-mark" src={icon} alt="" />}
      <div className="dash-hero-text">
        <h3>{first ? t('dashboard.welcome.named', { greeting, name: first }) : greeting}</h3>
        <p>
          {pendingCount > 0
            ? t('dashboard.welcome.pending', { count: pendingCount })
            : t('dashboard.welcome.allClear')}
        </p>
      </div>
      {pendingCount > 0 && (
        <Link to="/submissions" className="dash-hero-action">
          {t('dashboard.welcome.review')}
        </Link>
      )}
    </div>
  );
}
